import { Link } from 'react-router-dom';
import { useTranslation } from '../lib/i18n';
import { Shield, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function Legal() {
  const { language } = useTranslation();

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <header className="border-b border-white/5 bg-[#050505]/90 backdrop-blur-lg sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <Shield className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
            <span className="font-heading text-lg tracking-tight">SheetArmor</span>
          </Link>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-16">
        <Link to="/">
          <Button variant="ghost" className="text-neutral-400 hover:text-white mb-8 -ml-2">
            <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
            {language === 'fr' ? 'Retour' : 'Back'}
          </Button>
        </Link>

        <h1 className="font-heading text-4xl md:text-5xl font-light tracking-tight mb-8">
          {language === 'fr' ? 'Mentions Légales' : 'Legal Notice'}
        </h1>

        {language === 'fr' ? (
          <div className="space-y-8 text-neutral-300 font-body text-base leading-relaxed">
            <section>
              <h2 className="text-2xl font-heading text-white mb-4">Éditeur du Site</h2>
              <p><strong className="text-white">Raison sociale :</strong> SheetArmor</p>
              <p><strong className="text-white">Contact :</strong> hello@sheetarmor.io</p>
              <p><strong className="text-white">Forme juridique :</strong> Entreprise individuelle</p>
            </section>

            <section>
              <h2 className="text-2xl font-heading text-white mb-4">Directeur de la Publication</h2>
              <p>Le directeur de la publication est le représentant légal de SheetArmor.</p>
              <p>Contact : hello@sheetarmor.io</p>
            </section>

            <section>
              <h2 className="text-2xl font-heading text-white mb-4">Hébergement</h2>
              <p><strong className="text-white">Frontend (Application Web) :</strong></p>
              <p>Vercel Inc.</p>
              <p>440 N Barranca Ave #4133</p>
              <p>Covina, CA 91723, États-Unis</p>
              <p>Site web : <a href="https://vercel.com" className="text-blue-400 hover:text-blue-300 underline" target="_blank" rel="noopener noreferrer">vercel.com</a></p>

              <p className="mt-4"><strong className="text-white">Backend (API & Traitement) :</strong></p>
              <p>Render Networks Inc.</p>
              <p>525 Brannan St, Suite 300</p>
              <p>San Francisco, CA 94107, États-Unis</p>
              <p>Site web : <a href="https://render.com" className="text-blue-400 hover:text-blue-300 underline" target="_blank" rel="noopener noreferrer">render.com</a></p>
            </section>

            <section>
              <h2 className="text-2xl font-heading text-white mb-4">Propriété Intellectuelle</h2>
              <p>L'ensemble du contenu de ce site (textes, images, code source, design, logo, marque « SheetArmor ») est la propriété exclusive de SheetArmor, sauf mention contraire.</p>
              <p>Toute reproduction, distribution ou utilisation non autorisée est strictement interdite et constitue une contrefaçon sanctionnée par les articles L.335-2 et suivants du Code de la Propriété Intellectuelle.</p>
            </section>

            <section>
              <h2 className="text-2xl font-heading text-white mb-4">Protection des Données Personnelles</h2>
              <p>Pour toute question relative au traitement de vos données personnelles, veuillez consulter notre <Link to="/privacy" className="text-blue-400 hover:text-blue-300 underline">Politique de Confidentialité</Link>.</p>
              <p>Conformément au RGPD (Règlement Général sur la Protection des Données), vous disposez d'un droit d'accès, de rectification, de suppression et de portabilité de vos données personnelles.</p>
              <p>Pour exercer ces droits, contactez : hello@sheetarmor.io</p>
            </section>

            <section>
              <h2 className="text-2xl font-heading text-white mb-4">Cookies</h2>
              <p>SheetArmor utilise des cookies strictement nécessaires au fonctionnement du service (authentification sécurisée via HTTP-only session cookies).</p>
              <p>Aucun cookie publicitaire ou de suivi tiers n'est utilisé.</p>
            </section>

            <section>
              <h2 className="text-2xl font-heading text-white mb-4">Limitation de Responsabilité</h2>
              <p>SheetArmor met tout en œuvre pour offrir un service fiable et sécurisé. Cependant, l'éditeur ne saurait être tenu responsable :</p>
              <ul className="list-disc list-inside ml-4 space-y-2 mt-2">
                <li>Des interruptions ou dysfonctionnements temporaires du service</li>
                <li>Des décisions prises sur la base des rapports générés par notre outil (l'audit ne remplace pas un conseil professionnel)</li>
                <li>De l'inexactitude ou de l'incomplétude des analyses effectuées, bien que nous utilisions les technologies les plus avancées</li>
                <li>Des dommages indirects résultant de l'utilisation du service</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-heading text-white mb-4">Droit Applicable</h2>
              <p>Les présentes mentions légales sont régies par le droit français.</p>
              <p>En cas de litige, et à défaut d'accord amiable, compétence est attribuée aux tribunaux français.</p>
            </section>

            <section>
              <h2 className="text-2xl font-heading text-white mb-4">Contact</h2>
              <p>Pour toute question concernant ces mentions légales, vous pouvez nous contacter à l'adresse : <a href="mailto:hello@sheetarmor.io" className="text-blue-400 hover:text-blue-300 underline">hello@sheetarmor.io</a></p>
            </section>

            <p className="text-neutral-500 text-sm mt-12">Dernière mise à jour : Mars 2026</p>
          </div>
        ) : (
          <div className="space-y-8 text-neutral-300 font-body text-base leading-relaxed">
            <section>
              <h2 className="text-2xl font-heading text-white mb-4">Site Publisher</h2>
              <p><strong className="text-white">Company name:</strong> SheetArmor</p>
              <p><strong className="text-white">Contact:</strong> hello@sheetarmor.io</p>
              <p><strong className="text-white">Legal form:</strong> Sole proprietorship</p>
            </section>

            <section>
              <h2 className="text-2xl font-heading text-white mb-4">Publication Director</h2>
              <p>The publication director is the legal representative of SheetArmor.</p>
              <p>Contact: hello@sheetarmor.io</p>
            </section>

            <section>
              <h2 className="text-2xl font-heading text-white mb-4">Hosting</h2>
              <p><strong className="text-white">Frontend (Web Application):</strong></p>
              <p>Vercel Inc.</p>
              <p>440 N Barranca Ave #4133</p>
              <p>Covina, CA 91723, United States</p>
              <p>Website: <a href="https://vercel.com" className="text-blue-400 hover:text-blue-300 underline" target="_blank" rel="noopener noreferrer">vercel.com</a></p>

              <p className="mt-4"><strong className="text-white">Backend (API & Processing):</strong></p>
              <p>Render Networks Inc.</p>
              <p>525 Brannan St, Suite 300</p>
              <p>San Francisco, CA 94107, United States</p>
              <p>Website: <a href="https://render.com" className="text-blue-400 hover:text-blue-300 underline" target="_blank" rel="noopener noreferrer">render.com</a></p>
            </section>

            <section>
              <h2 className="text-2xl font-heading text-white mb-4">Intellectual Property</h2>
              <p>All content on this site (texts, images, source code, design, logo, "SheetArmor" brand) is the exclusive property of SheetArmor, unless otherwise stated.</p>
              <p>Any unauthorized reproduction, distribution, or use is strictly prohibited and constitutes infringement under applicable intellectual property laws.</p>
            </section>

            <section>
              <h2 className="text-2xl font-heading text-white mb-4">Personal Data Protection</h2>
              <p>For any questions regarding the processing of your personal data, please consult our <Link to="/privacy" className="text-blue-400 hover:text-blue-300 underline">Privacy Policy</Link>.</p>
              <p>In accordance with GDPR (General Data Protection Regulation), you have the right to access, rectify, delete, and port your personal data.</p>
              <p>To exercise these rights, contact: hello@sheetarmor.io</p>
            </section>

            <section>
              <h2 className="text-2xl font-heading text-white mb-4">Cookies</h2>
              <p>SheetArmor uses cookies strictly necessary for the operation of the service (secure authentication via HTTP-only session cookies).</p>
              <p>No advertising or third-party tracking cookies are used.</p>
            </section>

            <section>
              <h2 className="text-2xl font-heading text-white mb-4">Limitation of Liability</h2>
              <p>SheetArmor makes every effort to provide a reliable and secure service. However, the publisher cannot be held responsible for:</p>
              <ul className="list-disc list-inside ml-4 space-y-2 mt-2">
                <li>Temporary service interruptions or malfunctions</li>
                <li>Decisions made based on reports generated by our tool (audit does not replace professional advice)</li>
                <li>Inaccuracy or incompleteness of analyses performed, although we use the most advanced technologies</li>
                <li>Indirect damages resulting from the use of the service</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-heading text-white mb-4">Applicable Law</h2>
              <p>These legal notices are governed by French law.</p>
              <p>In the event of a dispute, and in the absence of an amicable agreement, jurisdiction is granted to French courts.</p>
            </section>

            <section>
              <h2 className="text-2xl font-heading text-white mb-4">Contact</h2>
              <p>For any questions regarding these legal notices, you can contact us at: <a href="mailto:hello@sheetarmor.io" className="text-blue-400 hover:text-blue-300 underline">hello@sheetarmor.io</a></p>
            </section>

            <p className="text-neutral-500 text-sm mt-12">Last updated: March 2026</p>
          </div>
        )}
      </div>
    </div>
  );
}
